import { useCallback } from 'react';
import qz from 'qz-tray';
import { PrintDocType, usePrinterSettingStore } from 'src/store/usePrinterSettingStore';
import { useQzPrint } from 'src/hooks/useQzPrint';
import { getQzConnected } from 'src/hooks/useQzTray';
import {
	ItemLabelPrintData,
	printItemBulkLabelsAtOnce,
	fetchItemBulkLabelHtml,
} from 'src/apis/itemLabelApi';
import { shouldSkipLabelPrintInDev } from 'src/apis/labelApi';

const getErrorMessage = (err: unknown): string => {
	if (err instanceof Error) return err.message;
	return String(err);
};

/**
 * itemCd 기반 라벨 출력 훅
 * QZ 연결/프린터 설정/저수준 print()는 useQzPrint를 그대로 재사용하고,
 * 어떤 API를 호출해서 HTML을 받아올지(orchestration)만 새로 구현한다.
 */
export const useItemQzPrint = () => {
	const { print } = useQzPrint();
	const { getPrinter, getLabelSize } = usePrinterSettingStore();

	const printBulkItemLabel = useCallback(async (
		dataList: ItemLabelPrintData[],
		docType: PrintDocType = 'label1',
	): Promise<boolean> => {
		if (shouldSkipLabelPrintInDev()) return true;
		if (!dataList || dataList.length === 0) {
			alert('출력할 품목 라벨 데이터가 없습니다.');
			return false;
		}

		const useBrowserPrint =
			!getQzConnected() ||
			!qz.websocket.isActive() ||
			((docType === 'label1' || docType === 'label2') && !getPrinter(docType));

		if (useBrowserPrint) {
			try {
				await printItemBulkLabelsAtOnce(dataList);
				return true;
			} catch (err: unknown) {
				console.error('품목 라벨 일괄 출력 실패:', err);
				alert(`품목 라벨 출력 실패: ${getErrorMessage(err)}`);
				return false;
			}
		}

		try {
			const html = await fetchItemBulkLabelHtml(dataList);
			const ok = await print({
				docType,
				data: html,
				dataType: 'html',
				labelSize: getLabelSize(docType as 'label1' | 'label2'),
			});
			if (!ok) return false;
			return true;
		} catch (err: unknown) {
			console.error('품목 라벨 QZ 출력 실패, 브라우저 인쇄로 대체:', err);
			try {
				await printItemBulkLabelsAtOnce(dataList);
				alert('서버 프린터 설정 출력에 실패하여 브라우저 인쇄로 대체했습니다.');
				return true;
			} catch (fallbackErr: unknown) {
				alert(`품목 라벨 출력 실패: ${getErrorMessage(err) ?? getErrorMessage(fallbackErr)}`);
				return false;
			}
		}
	}, [print, getPrinter, getLabelSize]);

	return { printBulkItemLabel };
};
